// ══════════════════════════════════════════════════════
//  ProductMetrics.jsx — PrimaryLens 专属产品指标看板
//
//  Plausible 不能给你的：
//    - 最热门项目（哪些项目用户最关心）
//    - 项目阶段分布（库存里有多少 idea / mainnet 项目）
//    - 评分趋势（六维平均分变化）
//    - 回测胜率（高分项目真的涨了多少）
//    - 用户反馈分布
//    - 缓存命中率 + 省了多少美元
//    - 行为漏斗
//
//  数据源：/admin/product-metrics
// ══════════════════════════════════════════════════════
import React, { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Brain, Flame, PieChart as PieChartIcon, TrendingUp, Target, MessageSquare, Repeat } from 'lucide-react';

const PROXY = process.env.REACT_APP_PROXY_URL || 'https://primary-lens-production.up.railway.app';

const C = {
  bg: '#f9fafb', surface: '#ffffff', border: '#e5e7eb',
  text: '#111827', muted: '#6b7280',
  accent: '#6366f1', green: '#10b981', orange: '#f59e0b', red: '#ef4444', purple: '#8b5cf6',
};

const TIP = { contentStyle: { fontSize: 12, borderRadius: 8 }, itemStyle: { fontSize: 12 } };

// 项目阶段着色
const STAGE_COLOR = {
  idea: '#ef4444', devnet: '#f59e0b', testnet: '#eab308',
  mainnet: '#3b82f6', scaling: '#10b981', mature: '#059669',
  unknown: '#9ca3af',
};

// outcome label 着色
const OUTCOME_COLOR = {
  pumped: '#10b981', flat: '#9ca3af', dumped: '#f59e0b', rugged: '#ef4444',
  no_token_data: '#d1d5db', unknown: '#e5e7eb',
};

function Card({ title, subtitle, children, action }) {
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '14px 18px', marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color: color || C.text, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

const RANGES = [
  { key: '7d',  label: 'Last 7 D' },
  { key: '28d', label: 'Last 28 D' },
  { key: '91d', label: 'Last 91 D' },
];

export default function ProductMetrics({ userId, accessToken }) {
  const [range, setRange] = useState('28d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true); setError(null);
    fetch(`${PROXY}/admin/product-metrics?uid=${userId}&range=${range}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(r => r.json())
      .then(d => { if (d.error) setError(d.error); else setData(d); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [userId, range, accessToken]);
  useEffect(() => { load(); }, [load]);

  const rangeSelector = (
    <div style={{ display: 'flex', gap: 4 }}>
      {RANGES.map(r => (
        <button key={r.key} onClick={() => setRange(r.key)} style={{
          padding: '4px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
          background: r.key === range ? C.accent : C.surface,
          color:      r.key === range ? '#fff'     : C.text,
          border: `1px solid ${r.key === range ? C.accent : C.border}`,
          fontWeight: r.key === range ? 600 : 400,
        }}>{r.label}</button>
      ))}
    </div>
  );

  if (error) return <div style={{ padding: 24, color: C.red }}>Error: {error}</div>;
  if (!data) return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>{rangeSelector}</div>
      <div style={{ padding: 40, color: C.muted, textAlign: 'center' }}>{loading ? 'Loading…' : '暂无数据'}</div>
    </div>
  );

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div style={{ fontSize: 13, color: C.muted }}>
          过去 <strong>{data.days}</strong> 天的产品指标 · sample: {data.cacheStats?.totalAnalyzed || 0} analyses
        </div>
        {rangeSelector}
      </div>

      {/* Cache stats — 上方核心 KPI */}
      <Card title={<><Brain size={13} strokeWidth={2} /> 缓存效率</>} subtitle="省下的 AI 调用成本">
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Stat label="总分析次数" value={data.cacheStats?.totalAnalyzed ?? 0} />
          <Stat label="命中缓存"   value={data.cacheStats?.fromCache ?? 0} color={C.green} />
          <Stat label="命中率"     value={data.cacheStats?.cacheHitRate != null ? `${data.cacheStats.cacheHitRate}%` : '—'} color={C.accent} />
          <Stat label="估算省下"   value={`$${data.cacheStats?.cacheSavedUsd ?? 0}`} color={C.green} sub="$0.03/次估算" />
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* 最热门项目 */}
        <Card title={<><Flame size={13} strokeWidth={2} /> 最热门项目 Top 20</>} subtitle="按 project_analyzed 计数">
          {data.topProjects.length === 0 ? (
            <div style={{ padding: 30, color: C.muted, textAlign: 'center', fontSize: 12 }}>暂无数据</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.min(28 * data.topProjects.length + 40, 600)}>
              <BarChart data={data.topProjects} layout="vertical" margin={{ left: 90 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: C.text }} axisLine={false} tickLine={false} width={85} />
                <Tooltip {...TIP} />
                <Bar dataKey="count" fill={C.accent} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* 项目阶段分布 */}
        <Card title={<><PieChartIcon size={13} strokeWidth={2} /> 项目阶段分布</>} subtitle="project_context 库存">
          {data.stageDist.length === 0 ? (
            <div style={{ padding: 30, color: C.muted, textAlign: 'center', fontSize: 12 }}>暂无数据（项目画像需先合成）</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.stageDist} dataKey="count" nameKey="stage" cx="50%" cy="50%" outerRadius={80} label={({ stage, count }) => `${stage}: ${count}`}>
                  {data.stageDist.map((e, i) => (
                    <Cell key={i} fill={STAGE_COLOR[e.stage] || C.muted} />
                  ))}
                </Pie>
                <Tooltip {...TIP} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* 评分维度趋势 */}
        <Card title={<><TrendingUp size={13} strokeWidth={2} /> 评分维度均值</>} subtitle="近 7 天 vs 之前">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.avgDims}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
              <XAxis dataKey="dim" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
              <Tooltip {...TIP} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="earlier" fill="#cbd5e1" name="之前" radius={[3,3,0,0]} />
              <Bar dataKey="recent"  fill={C.accent} name="近 7 天" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* 回测胜率 */}
        <Card title={<><Target size={13} strokeWidth={2} /> 评分回测胜率</>} subtitle="高分 vs 低分项目实际表现">
          {(data.backtestSummary?.sampleSize || 0) === 0 ? (
            <div style={{ padding: 30, color: C.muted, textAlign: 'center', fontSize: 12 }}>
              回测数据积累中（需等 score-backtest GHA 跑过）
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
                <Stat label="高分项目（≥70）" value={`${data.backtestSummary.highScoreWinRate ?? '—'}%`} sub={`${data.backtestSummary.highScoreCount} samples`} color={C.green} />
                <Stat label="低分项目（<50）" value={`${data.backtestSummary.lowScoreWinRate ?? '—'}%`} sub={`${data.backtestSummary.lowScoreCount} samples`} color={C.red} />
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={data.backtestOutcomes} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={60} label={({ label, count }) => `${label}: ${count}`}>
                    {data.backtestOutcomes.map((e, i) => (
                      <Cell key={i} fill={OUTCOME_COLOR[e.label] || C.muted} />
                    ))}
                  </Pie>
                  <Tooltip {...TIP} />
                </PieChart>
              </ResponsiveContainer>
            </>
          )}
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* 用户反馈 */}
        <Card title={<><MessageSquare size={13} strokeWidth={2} /> 用户反馈</>} subtitle="evidence-based scoring 校准">
          {data.feedbackAccuracy.length === 0 ? (
            <div style={{ padding: 30, color: C.muted, textAlign: 'center', fontSize: 12 }}>暂无反馈</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.feedbackAccuracy}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                <XAxis dataKey="accuracy" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
                <Tooltip {...TIP} />
                <Bar dataKey="count" fill={C.purple} radius={[3,3,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* 行为漏斗 */}
        <Card title={<><Repeat size={13} strokeWidth={2} /> 用户行为漏斗</>} subtitle="转化路径">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={data.funnel} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
              <YAxis dataKey="step" type="category" tick={{ fontSize: 10, fill: C.text }} axisLine={false} tickLine={false} width={110} />
              <Tooltip {...TIP} />
              <Bar dataKey="count" fill={C.orange} radius={[0,4,4,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
